import { useCallback, useMemo } from "react";

import { useSelector } from "react-redux";

import { applySelectedRole } from "../auth/postLoginNavigation";
import {
  isServiceProviderPortalRole,
  ROLES,
  SERVICE_PROVIDER_PORTAL_ROLES,
} from "../helpers/constants";
import {
  decryptPayload,
  encryptPayload,
  navigateToDomain,
} from "../helpers/utils";

export const SERVICER_PORTAL_PROMPT_DISMISSED_KEY =
  "epServicerPortalPromptDismissed";

const parseStoredUserRoles = () => {
  try {
    const raw = localStorage.getItem("userRoles");
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter(Boolean) : [];
  } catch {
    return [];
  }
};

const resolveActiveRole = () =>
  decryptPayload(localStorage.getItem("roles")) ||
  localStorage.getItem("activeRole") ||
  "";

/**
 * Detect whether the signed-in consumer-site session can also use the
 * service-provider portal, and provide a cross-domain switch helper.
 */
export default function useServiceProviderPortalAccess() {
  const loginAsSpouse = useSelector(
    (state) => state?.userProfile?.loginAsSpouse,
  );
  const profileTypes = useSelector(
    (state) => state?.userProfile?.userProfileDetails?.data?.user_type,
  );

  const allRoles = useMemo(() => {
    const fromStorage = parseStoredUserRoles();
    const fromProfile = Array.isArray(profileTypes)
      ? profileTypes.filter(Boolean)
      : [];
    const active = resolveActiveRole();
    return [...new Set([...fromStorage, ...fromProfile, active].filter(Boolean))];
  }, [profileTypes]);

  const serviceProviderRole = useMemo(
    () => allRoles.find((role) => isServiceProviderPortalRole(role)) || null,
    [allRoles],
  );

  const hasCustomerRole = useMemo(
    () =>
      allRoles.includes(ROLES.customer) ||
      allRoles.includes(ROLES.secondaryUser),
    [allRoles],
  );

  const activeRoleIsServiceProvider = isServiceProviderPortalRole(
    resolveActiveRole(),
  );

  const canUseServiceProviderPortal = Boolean(
    serviceProviderRole && !loginAsSpouse,
  );

  const goToServiceProviderPortal = useCallback(() => {
    if (!serviceProviderRole) {
      return;
    }
    applySelectedRole(serviceProviderRole, allRoles, encryptPayload);
    navigateToDomain("clients");
  }, [allRoles, serviceProviderRole]);

  const stayOnConsumerSite = useCallback(() => {
    sessionStorage.setItem(SERVICER_PORTAL_PROMPT_DISMISSED_KEY, "1");
    if (hasCustomerRole) {
      const customerRole = allRoles.includes(ROLES.customer)
        ? ROLES.customer
        : ROLES.secondaryUser;
      applySelectedRole(customerRole, allRoles, encryptPayload);
      return { switchedToCustomer: true, hasCustomerRole: true };
    }
    return { switchedToCustomer: false, hasCustomerRole: false };
  }, [allRoles, hasCustomerRole]);

  const isPromptDismissed = useCallback(
    () =>
      typeof sessionStorage !== "undefined" &&
      sessionStorage.getItem(SERVICER_PORTAL_PROMPT_DISMISSED_KEY) === "1",
    [],
  );

  return {
    allRoles,
    serviceProviderRole,
    hasCustomerRole,
    activeRoleIsServiceProvider,
    canUseServiceProviderPortal,
    goToServiceProviderPortal,
    stayOnConsumerSite,
    isPromptDismissed,
    serviceProviderRoleLabels: SERVICE_PROVIDER_PORTAL_ROLES,
  };
}
